import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import { MetodoHttp, ProvedorConexao, ProvedorEndpoint, TipoApi } from '../../../../types/smartinteg';

// Funções que o backend resolve no path; qualquer outro ${x} vira o texto literal "x", sem erro
const FUNCOES_PATH = ['HOJE', 'ONTEM'];

@Component({
  selector: 'app-provedor-endpoints',
  templateUrl: './provedor-endpoints.component.html',
  styleUrls: ['./provedor-endpoints.component.css'],
  providers: [ConfirmationService]
})
export class ProvedorEndpointsComponent implements OnInit {

  provedorId!: string;
  conexaoId!: string;
  conexao: ProvedorConexao | null = null;
  endpoints: ProvedorEndpoint[] = [];
  loading: boolean = true;
  salvando: boolean = false;

  endpointDialog: boolean = false;
  isEditDialog: boolean = false;
  endpointEmEdicao: ProvedorEndpoint | null = null;

  form!: FormGroup;

  TipoApi = TipoApi;

  // Em propriedades porque "{{" e "${" no template seriam lidos como interpolação
  readonly placeholderPath = '/v1/itens?data=${ONTEM}';
  readonly dicaPath = 'Suporta ${HOJE} e ${ONTEM} (datas yyyy-MM-dd) e {{variavel}} (preenchida por um recurso de Iteração).';
  readonly avisoLoginTexto = 'O login (recurso de Autenticação) usa o path como está: não resolve ${...} nem {{...}}.';

  metodos = [
    { label: 'GET', value: MetodoHttp.GET },
    { label: 'POST', value: MetodoHttp.POST },
    { label: 'PUT', value: MetodoHttp.PUT },
    { label: 'DELETE', value: MetodoHttp.DELETE }
  ];

  // O runtime só sabe interpretar JSON e SOAP; os demais falham na execução, depois da chamada HTTP
  tiposApi = [
    { label: 'JSON', value: TipoApi.JSON, disabled: false },
    { label: 'SOAP', value: TipoApi.SOAP, disabled: false },
    { label: 'XML (não executa)', value: TipoApi.XML, disabled: true },
    { label: 'TEXT (não executa)', value: TipoApi.TEXT, disabled: true },
    { label: 'UNKNOWN (não executa)', value: TipoApi.UNKNOWN, disabled: true }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      descricao: [''],
      metodo: [MetodoHttp.GET, Validators.required],
      path: ['', Validators.required],
      tipoApi: [TipoApi.JSON, Validators.required],
      // SOAP
      soapAction: [''],
      xmlElementoItem: [''],
      xmlSoapResponse: [false],
      // Consumo de token (requerAutenticacao + config.TOKEN)
      requerAutenticacao: [false],
      tokenConsumido: [''],
      // Endpoint de login (config.tokenName, tokenPath e ContentType)
      geraToken: [false],
      tokenName: [''],
      tokenPath: [''],
      contentTypeLogin: ['']
    });

    this.form.get('geraToken')?.valueChanges.subscribe((gera: boolean) => this.aplicarValidadoresLogin(gera));

    this.provedorId = this.route.snapshot.paramMap.get('provedorId')!;
    this.conexaoId = this.route.snapshot.paramMap.get('conexaoId')!;
    if (this.provedorId && this.conexaoId) {
      this.carregarConexao();
      this.carregarEndpoints();
    } else {
      this.toastService.erro('Erro', 'Conexão não informada.');
      this.voltar();
    }
  }

  private aplicarValidadoresLogin(gera: boolean): void {
    for (const campo of ['tokenName', 'tokenPath']) {
      const control = this.form.get(campo);
      control?.setValidators(gera ? [Validators.required] : []);
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }

  carregarConexao(): void {
    this.smartintegService.buscarConexao(this.conexaoId).subscribe({
      next: (data) => this.conexao = data,
      error: (err) => this.toastService.erro('Erro', 'Falha ao buscar a conexão: ' + mensagemErroApi(err))
    });
  }

  carregarEndpoints(): void {
    this.loading = true;
    this.smartintegService.listarEndpointsPorConexao(this.conexaoId).subscribe({
      next: (data) => {
        this.endpoints = data;
        this.loading = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Falha ao carregar endpoints: ' + mensagemErroApi(err));
        this.loading = false;
      }
    });
  }

  novoEndpoint(): void {
    this.endpointEmEdicao = null;
    this.isEditDialog = false;
    this.form.reset({
      nome: '', descricao: '', metodo: MetodoHttp.GET, path: '', tipoApi: TipoApi.JSON,
      soapAction: '', xmlElementoItem: '', xmlSoapResponse: false,
      requerAutenticacao: false, tokenConsumido: '',
      geraToken: false, tokenName: '', tokenPath: '', contentTypeLogin: ''
    });
    this.aplicarValidadoresLogin(false);
    this.endpointDialog = true;
  }

  editarEndpoint(endpoint: ProvedorEndpoint): void {
    const config = endpoint.config ?? {};
    this.endpointEmEdicao = endpoint;
    this.isEditDialog = true;
    this.form.reset({
      nome: endpoint.nome,
      descricao: endpoint.descricao ?? '',
      metodo: endpoint.metodo,
      path: endpoint.path ?? '',
      tipoApi: endpoint.tipoApi,
      soapAction: endpoint.soapAction ?? '',
      xmlElementoItem: endpoint.xmlElementoItem ?? '',
      xmlSoapResponse: !!endpoint.xmlSoapResponse,
      // Endpoints antigos podem ter null (o PUT sem o campo gravava null); aqui vira um booleano explícito
      requerAutenticacao: !!endpoint.requerAutenticacao,
      tokenConsumido: config['TOKEN'] ?? '',
      geraToken: !!config['tokenName'],
      tokenName: config['tokenName'] ?? '',
      tokenPath: config['tokenPath'] ?? '',
      contentTypeLogin: config['ContentType'] ?? ''
    });
    this.aplicarValidadoresLogin(!!config['tokenName']);
    this.endpointDialog = true;
  }

  // O PUT do backend substitui o config inteiro: parte do carregado para preservar chaves que a tela não
  // conhece, e só mexe nas quatro que o runtime lê. As chaves conhecidas não podem ir vazias (o backend
  // devolve 400), então valores em branco removem a chave.
  private montarConfig(v: any): Record<string, any> {
    const config: Record<string, any> = { ...(this.endpointEmEdicao?.config ?? {}) };

    if (v.geraToken) {
      config['tokenName'] = String(v.tokenName).trim();
      config['tokenPath'] = String(v.tokenPath).trim();
      const contentType = String(v.contentTypeLogin ?? '').trim();
      if (contentType) config['ContentType'] = contentType; else delete config['ContentType'];
    } else {
      delete config['tokenName'];
      delete config['tokenPath'];
      delete config['ContentType'];
    }

    // TOKEN só é lido pelo runtime quando o endpoint requer autenticação
    if (v.requerAutenticacao) {
      const token = String(v.tokenConsumido ?? '').trim();
      if (token) config['TOKEN'] = token; else delete config['TOKEN'];
    }

    return config;
  }

  salvarEndpoint(): void {
    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    this.salvando = true;

    // O PUT substitui nome, descricao, path, tipoApi, metodo e config; os demais campos seguem "ausente mantém".
    // Os campos SOAP só são enviados quando o tipo é SOAP ("" limpa); fora dele os valores gravados são preservados.
    const base: Record<string, any> = {
      nome: v.nome,
      descricao: v.descricao || undefined,
      path: v.path,
      tipoApi: v.tipoApi,
      metodo: v.metodo,
      requerAutenticacao: !!v.requerAutenticacao,
      config: this.montarConfig(v)
    };
    if (v.tipoApi === TipoApi.SOAP) {
      base['soapAction'] = String(v.soapAction ?? '').trim();
      base['xmlElementoItem'] = String(v.xmlElementoItem ?? '').trim();
      base['xmlSoapResponse'] = !!v.xmlSoapResponse;
    }

    if (!this.isEditDialog) {
      this.smartintegService.criarEndpoint({ conexaoId: this.conexaoId, ...base }).subscribe({
        next: () => this.aposSalvar('Endpoint criado com sucesso'),
        error: (err) => this.aoFalharSalvar('criar', err)
      });
      return;
    }

    this.smartintegService.atualizarEndpoint({ id: this.endpointEmEdicao!.id, ...base }).subscribe({
      next: () => this.aposSalvar('Endpoint atualizado com sucesso'),
      error: (err) => this.aoFalharSalvar('atualizar', err)
    });
  }

  private aposSalvar(mensagem: string): void {
    this.toastService.sucesso('Sucesso', mensagem);
    this.salvando = false;
    this.endpointDialog = false;
    this.carregarEndpoints();
  }

  private aoFalharSalvar(acao: string, err: any): void {
    this.toastService.erro('Erro', `Erro ao ${acao} endpoint: ` + mensagemErroApi(err));
    this.salvando = false;
  }

  // A exclusão de endpoint é física e o backend não checa dependentes: comandos que o usam
  // ficariam com referência quebrada e falhariam na execução
  deletarEndpoint(endpoint: ProvedorEndpoint): void {
    this.smartintegService.listarComandos().subscribe({
      next: (comandos) => {
        const usos = comandos.filter(c => c.endpointId === endpoint.id);
        if (usos.length > 0) {
          this.toastService.alerta(
            'Atenção',
            `O endpoint "${endpoint.nome}" é usado por ${usos.length} comando(s). Remova-os antes de excluir o endpoint.`
          );
          return;
        }
        this.confirmarExclusao(endpoint);
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao verificar o uso do endpoint: ' + mensagemErroApi(err));
      }
    });
  }

  private confirmarExclusao(endpoint: ProvedorEndpoint): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o endpoint <strong>${endpoint.nome}</strong>? Esta ação é irreversível.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarEndpoint(endpoint.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Endpoint deletado com sucesso');
            this.carregarEndpoints();
          },
          error: (err) => {
            this.toastService.erro('Erro', 'Erro ao deletar endpoint: ' + mensagemErroApi(err));
            this.loading = false;
          }
        });
      }
    });
  }

  // ---------- Avisos não bloqueantes (o backend aceita, mas o resultado pode surpreender) ----------

  get avisoPath(): string | null {
    const path: string = this.form.get('path')?.value ?? '';
    if (!path) return null;
    if (!path.startsWith('/')) {
      return 'O path é concatenado à URL base da conexão; normalmente começa com "/".';
    }
    const desconhecidas = [...path.matchAll(/\$\{(\w+)\}/g)]
      .map(m => m[1])
      .filter(nome => !FUNCOES_PATH.includes(nome));
    if (desconhecidas.length > 0) {
      return `\${${desconhecidas[0]}} não é uma função conhecida (só \${HOJE} e \${ONTEM}): será substituído pelo texto "${desconhecidas[0]}".`;
    }
    return null;
  }

  get avisoDuplicado(): boolean {
    const metodo = this.form.get('metodo')?.value;
    const path = this.form.get('path')?.value;
    if (!metodo || !path) return false;
    return this.endpoints.some(e =>
      e.id !== this.endpointEmEdicao?.id && e.metodo === metodo && e.path === path);
  }

  // O login do recurso de Autenticação usa o path cru: não resolve ${...} nem {{...}}
  get avisoPathLogin(): boolean {
    const path: string = this.form.get('path')?.value ?? '';
    return this.geraToken && (path.includes('${') || path.includes('{{'));
  }

  // Nomes de token gerados por endpoints de login desta conexão, para sugerir no consumo
  get tokensConhecidos(): string[] {
    const nomes = this.endpoints
      .filter(e => e.id !== this.endpointEmEdicao?.id)
      .map(e => e.config?.['tokenName'])
      .filter((n): n is string => typeof n === 'string' && n.length > 0);
    return [...new Set(nomes)];
  }

  get avisoTokenDesconhecido(): boolean {
    const token = String(this.form.get('tokenConsumido')?.value ?? '').trim();
    return this.requerAutenticacao && token.length > 0 && !this.tokensConhecidos.includes(token);
  }

  get tipoApiSelecionado(): TipoApi {
    return this.form.get('tipoApi')?.value;
  }

  get requerAutenticacao(): boolean {
    return !!this.form.get('requerAutenticacao')?.value;
  }

  get geraToken(): boolean {
    return !!this.form.get('geraToken')?.value;
  }

  get tokenConsumidoVazio(): boolean {
    return !String(this.form.get('tokenConsumido')?.value ?? '').trim();
  }

  campoInvalido(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && control.touched);
  }

  severidadeMetodo(metodo: MetodoHttp): string {
    switch (metodo) {
      case MetodoHttp.GET: return 'success';
      case MetodoHttp.POST: return 'info';
      case MetodoHttp.PUT: return 'warning';
      case MetodoHttp.DELETE: return 'danger';
      default: return 'secondary';
    }
  }

  // Resumo do papel do endpoint na coluna "Autenticação" da lista
  descricaoAutenticacao(endpoint: ProvedorEndpoint): string {
    const config = endpoint.config ?? {};
    const partes: string[] = [];
    if (config['tokenName']) partes.push(`Login (gera "${config['tokenName']}")`);
    if (endpoint.requerAutenticacao) {
      partes.push(config['TOKEN'] ? `Usa "${config['TOKEN']}"` : 'Usa a credencial da conexão');
    }
    return partes.length ? partes.join(' · ') : 'Não';
  }

  fecharDialog(): void {
    this.endpointDialog = false;
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/provedores', this.provedorId, 'conexoes']);
  }
}
